/**
 * Canonical source of truth for what the Showcase app displays about elizaOS.
 *
 * `getElizaShowcase()` is the single use case: it owns the curated facts and
 * every derived value (counts, the primitive index). Both the React UI and the
 * runtime plugin action read the DTO it returns and only format for output —
 * neither recomputes anything. Keeping derivation here is what stops the UI and
 * the agent action from drifting to different definitions of "how many
 * building blocks does an agent have".
 */

/** One of the five runtime building blocks a plugin can register. */
export interface ElizaPrimitive {
  readonly name: string;
  readonly summary: string;
}

/** A headline capability of the framework, shown as a feature card. */
export interface ElizaFeature {
  readonly title: string;
  readonly detail: string;
}

/** A single labelled figure rendered in the stats strip. */
export interface ElizaStat {
  readonly label: string;
  readonly value: string;
}

/** The full, display-ready description of elizaOS handed to any consumer. */
export interface ElizaShowcase {
  readonly name: string;
  readonly tagline: string;
  readonly description: string;
  readonly primitives: readonly ElizaPrimitive[];
  readonly features: readonly ElizaFeature[];
  readonly stats: readonly ElizaStat[];
  readonly repositoryUrl: string;
}

const PRIMITIVES: readonly ElizaPrimitive[] = [
  { name: "Actions", summary: "Things the agent can do — a validate check plus a handler." },
  { name: "Providers", summary: "Context injected into the prompt before the model runs." },
  { name: "Services", summary: "Long-lived singletons: clients, schedulers, connectors." },
  { name: "Evaluators", summary: "Post-response processing that runs after the agent replies." },
  { name: "Model handlers", summary: "The model-agnostic layer that swaps LLM providers." },
];

const FEATURES: readonly ElizaFeature[] = [
  {
    title: "Local-first",
    detail: "Every device runs a full agent on its own; connecting to the cloud is optional.",
  },
  {
    title: "Plugin architecture",
    detail: "A plugin is one module exporting actions, providers, services, and evaluators.",
  },
  {
    title: "Model-agnostic",
    detail: "Swap OpenAI, Anthropic, Google, Groq, or a local model behind one interface.",
  },
  {
    title: "Many connectors",
    detail: "Talk to the same agent from Discord, Telegram, iMessage, WhatsApp, X, or the web.",
  },
];

/**
 * Build the showcase DTO. Counts are derived from the source arrays here so the
 * stats strip can never disagree with the lists it summarizes.
 */
export function getElizaShowcase(): ElizaShowcase {
  const stats: readonly ElizaStat[] = [
    { label: "Runtime primitives", value: String(PRIMITIVES.length) },
    { label: "Headline features", value: String(FEATURES.length) },
    { label: "License", value: "MIT" },
  ];

  return {
    name: "elizaOS",
    tagline: "An open-source framework for building and deploying autonomous AI agents.",
    description:
      "elizaOS gives you a runtime, a plugin model, and message / memory / state " +
      "primitives with a model-agnostic LLM layer. You wire a runnable agent from " +
      "plugins, then talk to it from any connector — running entirely on your own " +
      "device or backed by the cloud.",
    primitives: PRIMITIVES,
    features: FEATURES,
    stats,
    repositoryUrl: "https://github.com/elizaOS/eliza",
  };
}

/**
 * Flatten the showcase DTO into a plain-text briefing. The runtime action uses
 * this so an agent describes elizaOS with the exact same facts the UI renders.
 */
export function formatShowcaseText(showcase: ElizaShowcase): string {
  const primitiveLines = showcase.primitives
    .map((primitive) => `- ${primitive.name}: ${primitive.summary}`)
    .join("\n");

  return `${showcase.name} — ${showcase.tagline}\n\n${showcase.description}\n\nRuntime primitives:\n${primitiveLines}`;
}
